export type CognitoOperation = 'profile' | 'session_check' | 'token_refresh'
export type CognitoTrigger =
  | 'bootstrap'
  | 'manual'
  | 'signed_in'
  | 'hub_refresh'
  | 'api_request'
  | 'graphql_auth'

export type CognitoDiagnostic = {
  app_id: 'storefront' | 'manage'
  build_version: string
  runtime: 'browser' | 'server'
  runtime_origin_class: 'local' | 'staging' | 'production' | 'other'
  cognito_environment: 'staging' | 'production' | 'unknown'
  route_template: string
  page_instance_id?: string
  operation: CognitoOperation
  trigger: CognitoTrigger
  reason: 'failure' | 'burst'
  occurred_at_utc: string
  window_seconds: number
  logical_calls_in_window: number
  refresh_signals_in_window: number
  duration_ms: number
  error_name?: string
  http_status?: number
  cognito_request_id?: string
}

type Context = Pick<
  CognitoDiagnostic,
  | 'app_id'
  | 'build_version'
  | 'runtime'
  | 'runtime_origin_class'
  | 'cognito_environment'
  | 'route_template'
  | 'page_instance_id'
>
const WINDOW_MS = 60_000
const BURST_THRESHOLD = 20
const GENERIC_ERROR_NAMES = new Set([
  'Error',
  'EvalError',
  'RangeError',
  'ReferenceError',
  'SyntaxError',
  'TypeError',
  'URIError',
  'AggregateError',
  'CognitoError',
])

export const getCognitoDiagnosticErrorName = (error: unknown) => {
  if (!error || typeof error !== 'object') return undefined
  const value = error as {
    name?: unknown
    _type?: unknown
    __type?: unknown
    type?: unknown
    code?: unknown
  }
  const identifiers = [
    value.name,
    value._type,
    value.__type,
    value.type,
    value.code,
  ]
    .filter((field): field is string => typeof field === 'string')
    .map((field) => field.trim().split('#').pop())
    .filter(
      (name): name is string =>
        typeof name === 'string' && /^[A-Za-z][A-Za-z0-9]{0,63}$/.test(name),
    )
  return (
    identifiers.find((name) => !GENERIC_ERROR_NAMES.has(name)) ?? identifiers[0]
  )
}

const errorFields = (error: unknown) => {
  if (!error || typeof error !== 'object') return {}
  const value = error as {
    metadata?: Record<string, unknown>
    $metadata?: Record<string, unknown>
  }
  const metadata = value.metadata ?? value.$metadata
  return {
    error_name: getCognitoDiagnosticErrorName(error) ?? 'CognitoError',
    ...(typeof metadata?.requestId === 'string' &&
    /^[A-Za-z0-9-]{1,128}$/.test(metadata.requestId)
      ? { cognito_request_id: metadata.requestId }
      : {}),
    ...(typeof metadata?.httpStatusCode === 'number' &&
    Number.isInteger(metadata.httpStatusCode) &&
    metadata.httpStatusCode >= 100 &&
    metadata.httpStatusCode <= 599
      ? { http_status: metadata.httpStatusCode }
      : {}),
  }
}

// Counters describe logical calls/signals, not the SDK's internal HTTP retries.
// At most one failure and one burst per operation per minute. No timers/storage.
export const createCognitoDiagnostics = (
  report: (diagnostic: CognitoDiagnostic) => void | Promise<void>,
  context: () => Context,
  now: () => number = Date.now,
  isExpectedError: (error: unknown) => boolean = (error) =>
    getCognitoDiagnosticErrorName(error) === 'UserUnAuthenticatedException',
) => {
  const windows = new Map<
    CognitoOperation,
    {
      start: number
      calls: number
      refreshSignals: number
      reported: { failure: boolean; burst: boolean }
    }
  >()

  const count = (operation: CognitoOperation) => {
    const time = now()
    let window = windows.get(operation)
    if (!window || time - window.start >= WINDOW_MS) {
      window = {
        start: time,
        calls: 0,
        refreshSignals: 0,
        reported: { failure: false, burst: false },
      }
      windows.set(operation, window)
    }
    window.calls += 1
    return window
  }

  const emit = (
    operation: CognitoOperation,
    trigger: CognitoTrigger,
    reason: 'failure' | 'burst',
    duration: number,
    error?: unknown,
  ) => {
    const window = windows.get(operation)
    if (!window || window.reported[reason]) return
    try {
      const diagnosticContext = context()
      // A server process handles many users: volume alone is not a page loop.
      if (reason === 'burst' && diagnosticContext.runtime === 'server') return
      window.reported[reason] = true
      const time = now()
      const refreshWindow = windows.get('token_refresh')
      const pending = report({
        ...diagnosticContext,
        operation,
        trigger,
        reason,
        occurred_at_utc: new Date(time).toISOString(),
        window_seconds: WINDOW_MS / 1000,
        logical_calls_in_window: window.calls,
        refresh_signals_in_window:
          operation === 'token_refresh'
            ? window.refreshSignals
            : refreshWindow && time - refreshWindow.start < WINDOW_MS
              ? refreshWindow.refreshSignals
              : 0,
        duration_ms: Math.max(0, duration),
        ...errorFields(error),
      })
      // Browser and Hub consumers stay nonblocking; always isolate rejections.
      if (pending) {
        const settled = Promise.resolve(pending).catch(() => {})
        if (diagnosticContext.runtime === 'server') return settled
      }
    } catch {
      // Diagnostic failures must never affect authentication.
    }
  }

  return {
    observe: async <T>(
      operation: CognitoOperation,
      trigger: CognitoTrigger,
      action: () => Promise<T>,
    ): Promise<T> => {
      const started = now()
      const window = count(operation)
      try {
        const result = await action()
        // A newer call may have advanced the minute while this action awaited.
        if (windows.get(operation) !== window) return result
        // Session checks can be frequent for normal GraphQL traffic. Only
        // profile checks and actual Hub renewals are eligible for burst alerts.
        if (operation !== 'session_check' && window.calls >= BURST_THRESHOLD)
          await emit(operation, trigger, 'burst', now() - started)
        return result
      } catch (error) {
        if (windows.get(operation) !== window) throw error
        try {
          // Reserve both budgets and capture counters before awaiting a reporter.
          const pending: (Promise<void> | undefined)[] = []
          if (!isExpectedError(error))
            pending.push(
              emit(operation, trigger, 'failure', now() - started, error),
            )
          if (operation !== 'session_check' && window.calls >= BURST_THRESHOLD)
            pending.push(
              emit(operation, trigger, 'burst', now() - started, error),
            )
          await Promise.all(pending)
        } catch {
          // Preserve the original rejection even for unusual error objects.
        }
        throw error
      }
    },
    refreshSignal: (error?: unknown) => {
      const window = count('token_refresh')
      window.refreshSignals += 1
      try {
        if (error !== undefined && !isExpectedError(error))
          emit('token_refresh', 'hub_refresh', 'failure', 0, error)
        if (window.calls >= BURST_THRESHOLD)
          emit('token_refresh', 'hub_refresh', 'burst', 0, error)
      } catch {
        // Diagnostics must not interrupt the consumer's Hub handler.
      }
    },
  }
}
